import React, { useRef, useEffect, useState } from "react"
import Slider from "react-slick"
import gsap from "gsap"
import { useGSAP } from "@gsap/react"
import { debounce } from "lodash"
import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"
import { TextElementNova } from "@nutrafol/nutrafol-ui-kit/dist/TextElementNova"

import {
  SlideWrapper,
  SlideTrack,
  Slide,
  Divider,
  CauseText,
  Dots,
  Dot,
  AnimationArea,
  BottomSliderWrapper,
  SliderControls,
} from "./rootcauses.styled"

const HEIGHT = 260
const waves = [1, 20, 40, 60] // y values for the waves

function getFactor(value, values) {
  const max = Math.max(...values.map((v) => v.value))

  return value / max
}

const getShape = (y, factor) => {
  const computedY = HEIGHT + y - Math.abs(factor) * (HEIGHT - 1)
  return `M0 ${HEIGHT}C106 ${HEIGHT} 89 ${computedY} 150 ${computedY}C211 ${computedY} 193 ${HEIGHT} 300 ${HEIGHT}`
}

const Wave = ({ y, index, factor, isActive }) => {
  const waveRef = useRef(null)

  useGSAP(() => {
    if (waveRef.current && isActive) {
      gsap.fromTo(
        waveRef.current,
        {
          attr: { d: getShape(y + 10, factor) },
        },
        {
          duration: 1,
          attr: { d: getShape(y, factor) },
          ease: "circ.out",
          delay: 1,
        }
      )
    }
    if (waveRef.current && !isActive) {
      gsap.fromTo(
        waveRef.current,
        {
          attr: { d: getShape(y, factor) },
        },
        {
          duration: 1,
          attr: { d: getShape(y + 10, factor) },
          ease: "circ.out",
          delay: 1,
        }
      )
    }
  }, [isActive])

  return (
    <path
      shapeRendering="geometricPrecision"
      d={getShape(y + 10, factor)}
      strokeLinejoin="round"
      ref={waveRef}
      stroke="#fff"
      strokeWidth="1"
      opacity={1 - index * 0.25}
    />
  )
}

export default function WavesSlider({
  causes,
  activeIndex,
  setActiveIndex,
  disabled,
  setDisabled,
  disableDuringAnimation,
}) {
  const wrapperRef = useRef(null)
  const sliderRef = useRef(null)
  const causeTextRef = useRef(null)
  const dotRef = useRef(null)
  const [longestDescription, setLongestDescription] = useState("")

  useEffect(() => {
    const longestDescCause = causes.reduce(function (a, b) {
      return a.longDescription.length > b.longDescription.length ? a : b
    })
    setLongestDescription(longestDescCause.longDescription)
  }, [causes])

  useGSAP(() => {
    const tl = gsap.timeline({})

    tl.fromTo(
      causeTextRef.current,
      {
        opacity: 1,
      },
      {
        opacity: 0,
        duration: 0.3,
      }
    ).fromTo(
      causeTextRef.current,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: 0.3,
        delay: 0.5,
      },
      1
    )
  })

  const setDotAndTextPos = () => {
    if (!causeTextRef.current || !dotRef.current) return
    const sliderHeight = sliderRef.current.innerSlider.list.offsetHeight - 150
    const yBottomPos =
      sliderHeight * getFactor(causes[activeIndex].value, causes) -
      causes[activeIndex].value

    gsap.to(dotRef.current, {
      ease: "circ.out",
      duration: 1,
      y: -Math.abs(yBottomPos) - dotRef.current.offsetHeight / 2,
    })
  }

  useGSAP(
    () => {
      setTimeout(() => {
        setDotAndTextPos()
      })
    },
    { dependencies: [activeIndex, causes] }
  )

  useEffect(() => {
    if (typeof window === "undefined") return
    const debouncedSetDotAndTextPos = debounce(() => setDotAndTextPos(), 500)
    window.addEventListener("resize", debouncedSetDotAndTextPos)
    sliderRef.current.slickGoTo(activeIndex)
    return () => window.removeEventListener("resize", debouncedSetDotAndTextPos)
  }, [activeIndex])

  const settings = {
    dots: false,
    infinite: true,
    slidesToScroll: 1,
    arrows: false,
    speed: 1000,
    slidesToShow: 1,
    centerMode: true,
    variableWidth: true,
    autoplay: false,
    touchMove: false,
    beforeChange: (_, index) => {
      setActiveIndex(index)
    },
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1.3,
          touchMove: true,
        },
      },
    ],
  }

  const handlePrevSlideClick = () => {
    if (activeIndex === 0 || disabled) return
    disableDuringAnimation()
    sliderRef.current.slickPrev()
    segmentEvent("Carousel Clicked", {
      module_location: "mid_fold",
      module_name: "Root Causes",
      slide_name: causes[activeIndex].name,
      slide_number: activeIndex + 1,
      arrow: "left",
    })
  }

  const handleNextSlideClick = () => {
    if (activeIndex === causes.length - 1 || disabled) return
    disableDuringAnimation()
    sliderRef.current.slickNext()
    segmentEvent("Carousel Clicked", {
      module_location: "mid_fold",
      module_name: "Root Causes",
      slide_name: causes[activeIndex].name,
      slide_number: activeIndex + 1,
      arrow: "right",
    })
  }

  return (
    <>
      <AnimationArea>
        <SlideWrapper ref={wrapperRef}>
          <CauseText
            ref={causeTextRef}
            className="bg-nova-base-black h-4 relative z-10 mb-8 sm:mb-[112px] md:mb-14"
          >
            <TextElementNova
              text={causes[activeIndex].name}
              className="Caption-10-Dia-Uppercase text-nova-base-white"
            />
          </CauseText>
          <Divider />
          <SlideTrack>
            <Slider {...settings} ref={sliderRef}>
              {causes.map((cause, i) => {
                const factor = getFactor(cause.value, causes)
                return (
                  <Slide key={i} width={[1 / 3, 1 / 3, 1 / 3, 1 / 2]}>
                    <svg
                      width="100%"
                      viewBox="0 0 300 261"
                      fill="none"
                      style={{ transform: "translate3d(0px, 0px, 0px)" }}
                    >
                      {waves.map((y, ii) => (
                        <Wave
                          key={ii}
                          isActive={i === activeIndex}
                          y={y}
                          index={ii}
                          factor={factor}
                        />
                      ))}
                    </svg>
                  </Slide>
                )
              })}
            </Slider>
            <Dots>
              <Dot ref={dotRef} />
              <Dot className="static" />
            </Dots>
          </SlideTrack>
        </SlideWrapper>
      </AnimationArea>
      <BottomSliderWrapper>
        <div className="text-wrap relative">
          <TextElementNova
            element="span"
            text={causes[activeIndex].longDescription}
            className="Body-20-Dia-Normal text-nova-base-white absolute top-0 left-0 right-0"
          />
          <TextElementNova
            element="span"
            text={longestDescription}
            className="Body-20-Dia-Normal text-nova-base-white block invisible"
          />
        </div>
        <SliderControls>
          <button
            className={`btn-prev ${activeIndex === 0 ? "disabled" : ""}`}
            onClick={handlePrevSlideClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.91711 4.32614C7.07332 4.16993 7.32658 4.16993 7.48279 4.32614C7.639 4.48235 7.639 4.73562 7.48279 4.89183L4.16622 8.2084H12.8C13.0209 8.2084 13.2 8.38748 13.2 8.6084C13.2 8.82931 13.0209 9.0084 12.8 9.0084H4.16505L7.48279 12.3261C7.639 12.4824 7.639 12.7356 7.48279 12.8918C7.32658 13.048 7.07332 13.048 6.91711 12.8918L2.91711 8.89183C2.7609 8.73562 2.7609 8.48235 2.91711 8.32614L6.91711 4.32614Z"
              />
            </svg>
          </button>
          <TextElementNova
            text={`${activeIndex + 1} / ${causes.length}`}
            className="Body-20-Dia-Normal text-nova-base-white"
          />
          <button
            className={`btn-next ${
              activeIndex === causes.length - 1 ? "disabled" : ""
            }`}
            onClick={handleNextSlideClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="17"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.08289 4.32614C8.92668 4.16993 8.67342 4.16993 8.51721 4.32614C8.361 4.48235 8.361 4.73562 8.51721 4.89183L11.8338 8.2084H3.20005C2.97913 8.2084 2.80005 8.38748 2.80005 8.6084C2.80005 8.82931 2.97913 9.0084 3.20005 9.0084H11.8349L8.51721 12.3261C8.361 12.4824 8.361 12.7356 8.51721 12.8918C8.67342 13.048 8.92668 13.048 9.08289 12.8918L13.0829 8.89183C13.2391 8.73562 13.2391 8.48235 13.0829 8.32614L9.08289 4.32614Z"
              />
            </svg>
          </button>
        </SliderControls>
      </BottomSliderWrapper>
    </>
  )
}
